/* Write a skill, or a new version of one the workspace made: SKILL.md's fields, any
   other files, Save. A new one starts from the template; an edit opens the skill's
   files, keeps its name and names the version it started from, so a save over a newer
   one is refused rather than lost (tasks/custom-skills/01-design-mock.md). Unsaved work
   is never lost: Back and a reload ask first, and leaving the page any other way keeps
   the draft for when the person comes back (`keptDraft.ts`). */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { skillsKeys } from "@/shared/api/skills";
import { TextArea, confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { BrandButton, Chip, GhostButton, PageHeader } from "@/shared/components/ui-kit";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { SKILL_CODES } from "@/shared/lib/skill-rules";

import { formatBytes } from "../catalog";
import {
  SKILL_MD_TEMPLATE, buildSkillMd, descriptionIssue, draftForm, folderForm, nameIssue, problemsOf, splitSkillMd,
  textFile, tooBigToSend, type DraftFile, type SkillMd,
} from "../folder";
import { useDraftFiles } from "../hooks/useDraftFiles";
import { useSkillToEdit, type SkillToEdit } from "../hooks/useSkillToEdit";
import { dropDraft, keepDraft, keptDraft, useAccount } from "../keptDraft";
import { useCheckSkill, useCreateSkill, useUpdateSkill } from "../hooks/useSkillWrites";
import { LoadingDots, ProblemLines } from "./atoms";
import EditorTree from "./EditorTree";
import SkillMdFields from "./SkillMdFields";

import type { ApiError } from "@/shared/api/errors";

export default function SkillEditor({ editId, onBack, onSaved }: {
  /** A workspace skill to edit; absent, a new one. */
  editId?: string;
  onBack: () => void;
  onSaved: (skillId: string) => void;
}) {
  const opened = useSkillToEdit(editId);
  if (!editId) return <Editor start={null} onBack={onBack} onSaved={onSaved} />;
  if (opened.isPending) return <><Header title="Edit skill" onBack={onBack} /><LoadingDots label="Opening the skill" /></>;
  if (opened.isError) {
    // a skill that's gone takes its kept draft with it: it has nothing left to save into
    const gone = (opened.error as ApiError).status === 404;
    return (
      <>
        <Header title="Edit skill" onBack={() => { if (gone) dropDraft(); onBack(); }} />
        <div className="py-16 text-center text-[13px] text-[var(--text-secondary)]">Couldn't open this skill: {(opened.error as ApiError).message}</div>
      </>
    );
  }
  // every read of the skill (Reopen) starts the editor over on it
  return <Editor key={opened.dataUpdatedAt} start={opened.data} onBack={onBack} onSaved={onSaved} />;
}

function Header({ title, mono = false, editing = false, onBack, children }: {
  title: string;
  /** The title is a skill's name, which reads as code. */
  mono?: boolean;
  editing?: boolean;
  onBack: () => void;
  children?: React.ReactNode;
}) {
  return (
    <PageHeader title={
      <Breadcrumb parent={{ label: "Skills", onClick: onBack }}>
        <span className={`truncate ${mono ? "font-mono" : ""}`}>{title}</span>
        {editing && <Chip>Editing</Chip>}
      </Breadcrumb>
    }
    >
      {children}
    </PageHeader>
  );
}

/** What a save compares against to know whether anything changed. */
const snapshot = (md: SkillMd, files: DraftFile[]) =>
  JSON.stringify([md, files.map((f) => [f.path, f.text, f.blob ? f.size : null])]);

function Editor({ start, onBack, onSaved }: {
  start: SkillToEdit | null;
  onBack: () => void;
  onSaved: (skillId: string) => void;
}) {
  const qc = useQueryClient();
  const account = useAccount();
  const editing = start?.skill ?? null;
  const skillId = editing?.id ?? null;
  // work left here unsaved earlier, picked up where it stopped
  const [restored] = useState(() => {
    const kept = keptDraft(account);
    return kept?.skillId === skillId ? kept : null;
  });
  const [md, setMd] = useState<SkillMd>(restored?.md ?? start?.md ?? SKILL_MD_TEMPLATE);
  // a save names the version the work started from: a kept draft's, else the one just opened
  const [base] = useState(() => restored?.version ?? editing?.version ?? null);
  const [tried, setTried] = useState(false);
  const [tooBig, setTooBig] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const create = useCreateSkill();
  const update = useUpdateSkill();
  const read = useCheckSkill();
  const save = editing ? update : create;
  const error = save.error as ApiError | null;
  const problems = problemsOf(error);

  // An edit after a refused save makes the refusal stale: the next save checks again
  const changed = () => {
    if (save.isError) save.reset();
    setTooBig(false);
  };
  const editMd = (next: SkillMd) => {
    changed();
    setMd(next);
  };
  const folder = useDraftFiles({
    start: restored?.files ?? start?.files ?? [],
    startOpen: restored?.open ?? "SKILL.md",
    onChange: changed,
    // read as the server reads it; an edit keeps the name, because agents call it by it
    onSkillMd: async (text) => {
      const found = await read.mutateAsync(folderForm([{ path: "SKILL.md", body: new Blob([text]) }]));
      editMd({ name: editing ? editing.slug : found.name ?? "", description: found.description ?? "", ...splitSkillMd(text) });
    },
  });
  const { files, open } = folder;

  const initial = useMemo(() => snapshot(start?.md ?? SKILL_MD_TEMPLATE, start?.files ?? []), [start]);
  const dirty = snapshot(md, files) !== initial;

  useEffect(() => {
    if (!dirty) dropDraft();
    else if (account) keepDraft({ account, skillId, version: base, md, files, open });
  }, [dirty, account, skillId, base, md, files, open]);
  // The editor is on screen: a save that lands after the person left drops the work and says so,
  // but doesn't take them back to Skills.
  const shown = useRef(true);
  useEffect(() => {
    shown.current = true;
    return () => { shown.current = false; };
  }, []);

  // a reload or a closed tab with unsaved work asks too
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // untouched fields keep SKILL.md byte for byte: rebuilding it would rewrite its frontmatter
  const skillMd = start && JSON.stringify(md) === JSON.stringify(start.md) ? start.skillMd : buildSkillMd(md);
  const all: DraftFile[] = [textFile("SKILL.md", skillMd), ...files];
  const openFile = files.find((f) => f.path === open) ?? null;

  const submit = () => {
    setTried(true);
    if (nameIssue(md.name) || descriptionIssue(md.description)) {
      folder.setOpen("SKILL.md");
      return;
    }
    if (tooBigToSend(all)) return setTooBig(true);
    const done = (savedId: string, said: string) => {
      toast.success(said);
      dropDraft();
      if (shown.current) onSaved(savedId);
    };
    // The promise, not `mutate`'s callbacks: those are dropped if the person leaves mid-save, and
    // the work kept on the way out would reopen and could be saved a second time.
    const saving = editing
      ? update.mutateAsync({ id: editing.id, form: draftForm(all, { base_version: base! }) }).then((skill) =>
        done(skill.id, skill.version === editing.version
          ? "Nothing changed, so no new version"
          : `Saved a new version of ${skill.name} — agents that have it can take the update`))
      : create.mutateAsync(draftForm(all)).then((skill) => done(skill.id, `${skill.name} saved to the workspace`));
    saving.catch(() => undefined); // the mutation cache toasts why; the work stays in the editor
  };

  // the work goes, as the banner says: the editor opens again on the newer version
  const reopen = () => {
    dropDraft();
    qc.invalidateQueries({ queryKey: skillsKeys.toEdit(editing!.id) });
  };
  // a save in flight lands whatever is said here, so leaving waits for it
  const leave = () => (save.isPending ? undefined : dirty ? setLeaving(true) : onBack());
  const known = new Set(all.map((f) => f.path));
  const flagged = new Set(problems.map((p) => p.path).filter((p): p is string => !!p && known.has(p)));
  // a problem about no file the editor holds (a path the server read differently) is the folder's
  const whole = problems.filter((p) => !p.path || !known.has(p.path));

  return (
    <>
      <Header title={editing ? editing.slug : md.name || "New skill"} mono={!!(editing || md.name)} editing={!!editing} onBack={leave}>
        {dirty && (
          <span className="shrink-0 hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-[var(--text-tertiary)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--warn)]" /> Unsaved changes
          </span>
        )}
        <BrandButton onClick={submit} disabled={save.isPending || (!!editing && !dirty)} className="disabled:opacity-40 disabled:pointer-events-none">
          {save.isPending && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
          {editing ? "Save version" : "Create skill"}
        </BrandButton>
      </Header>

      {error && (error.code === SKILL_CODES.changed ? (
        <div role="alert" className="shrink-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-2.5 bg-[var(--warn-bg)] border-b border-[var(--warn-ring)] text-[12.5px] text-[var(--warn-text)]">
          <span className="min-w-0 flex-1">{error.message} Copy anything you want to keep, then reopen it.</span>
          <GhostButton onClick={reopen} className="h-7 px-2.5">Reopen</GhostButton>
        </div>
      ) : (
        <div role="alert" className="shrink-0 px-5 py-2.5 bg-[var(--danger-bg)] border-b border-[var(--danger-ring)] text-[12.5px] text-[var(--danger)] space-y-0.5">
          {problems.length ? (
            <>
              <div className="font-medium">
                Not saved: {problems.length === 1 ? "one problem" : `${problems.length} problems`}
                {problems.length > whole.length && ", marked on the fields and files they're about"}.
              </div>
              <ProblemLines problems={whole} withPath />
            </>
          ) : error.message}
        </div>
      ))}
      {tooBig && (
        <div role="alert" className="shrink-0 px-5 py-2.5 bg-[var(--danger-bg)] border-b border-[var(--danger-ring)] text-[12.5px] text-[var(--danger)]">
          Not saved: over 10 MB in all, more than one save can send. Upload the folder as a .zip instead — it can unpack to 20 MB.
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        <EditorTree
          files={all}
          open={open}
          flagged={flagged}
          onOpen={folder.setOpen}
          onAdd={folder.add}
          onUpload={(list) => folder.upload(list).catch((e: unknown) => toast.error(`Couldn't read those files: ${e instanceof Error ? e.message : String(e)}`))}
          onRename={folder.rename}
          onRemove={folder.remove}
        />
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden">
          {open === "SKILL.md" || !openFile ? (
            <SkillMdFields
              md={md}
              onChange={editMd}
              nameFixed={!!editing}
              problems={problems.filter((p) => p.path === "SKILL.md")}
              showIssues={tried}
            />
          ) : (
            <FilePane file={openFile} problems={problems.filter((p) => p.path === openFile.path)} onText={(text) => folder.write(openFile.path, text)} />
          )}
        </div>
      </div>

      <AlertDialog open={leaving} onOpenChange={setLeaving}>
        <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {editing ? "Your changes to this skill aren't saved, and they go if you leave." : "This skill isn't saved yet, and it goes if you leave."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full text-[13px]">Keep editing</AlertDialogCancel>
            <button className={confirmDangerCls} onClick={() => { setLeaving(false); dropDraft(); onBack(); }}>Discard</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Any file but SKILL.md: text edited as it is, anything else kept as uploaded. */
function FilePane({ file, problems, onText }: {
  file: DraftFile;
  problems: ReturnType<typeof problemsOf>;
  onText: (text: string) => void;
}) {
  return (
    <div className="max-w-[960px] mx-auto px-4 md:px-6 py-6 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[12.5px] text-[var(--text-primary)] break-all">{file.path}</span>
        <span className="shrink-0 font-mono tabular-nums text-[11.5px] text-[var(--text-tertiary)]">{formatBytes(file.size)}</span>
      </div>
      <ProblemLines problems={problems} />
      {file.text != null ? (
        <TextArea
          value={file.text}
          onChange={onText}
          aria-label={`Contents of ${file.path}`}
          spellCheck={false}
          className="min-h-[480px] resize-y px-3.5 py-3 font-mono text-[16px] sm:text-[12.5px] leading-[1.65]"
        />
      ) : (
        <div className="tray-box px-4 py-6 text-center text-[12.5px] text-[var(--text-tertiary)]">
          Not text, so it's kept exactly as uploaded. Upload a file at the same path to replace it.
        </div>
      )}
    </div>
  );
}
